import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/shad';
import { PropsWithChildren } from 'react';
import { WithStyle } from '@/types/component';
import { IconType } from '@/constants/icons';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';
import { TotalsChip } from '@/domains/home/components/home-tabs/_components/base-chip';

export const DEFAULT_ROUNDED = 'rounded-xl';

const buttonVariants = cva(
  `inline-flex items-center gap-2 font-semibold justify-center whitespace-nowrap ${DEFAULT_ROUNDED} text-sm transition-colors focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 rounded-md`,
  {
    variants: {
      variant: {
        default: `${DEFAULT_ROUNDED} bg-primary/90 border text-primary-foreground shadow-sm hover:bg-primary hover:shadow-sm hover:shadow-white/40 `,
        defaultDark:
          'bg-background text-white shadow-sm hover:bg-primary/90 hover:bg-neutral-500/30 border border-neutral-800',
        negative: `${DEFAULT_ROUNDED} bg-negative text-negative-foreground shadow-xs hover:bg-negative/90`,
        neutral: `${DEFAULT_ROUNDED} bg-neutral text-yellow-900 shadow-xs hover:bg-neutral/90`,
        positive: 'bg-positive text-positive-foreground shadow-xs hover:bg-positive/90',
        destructive: `${DEFAULT_ROUNDED} bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90`,
        outline: `${DEFAULT_ROUNDED} border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground font-semibold`,
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground ',
        pill: 'hover:bg-accent/50 hover:text-accent-foreground rounded-full',
        link: 'text-primary underline-offset-4 hover:underline',
        purple: `${DEFAULT_ROUNDED} bg-icon flex items-center gap-2 px-2 py-1.5 hover:brightness-90`,
        white: `${DEFAULT_ROUNDED} bg-white text-black flex items-center gap-2 px-2 py-1.5 hover:brightness-90`,
      },
      size: {
        default: 'h-8 px-2 py-2 text-[12px]',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 px-8',
        icon: 'h-9 w-9',
        iconSmall: 'h-6 w-6',
      },
      status: {
        active: '',
        inactive: 'opacity-50',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type UIButtonVariant = VariantProps<typeof buttonVariants>['variant'];

export type ButtonProps = PropsWithChildren &
  React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> &
  WithStyle & {
    asChild?: boolean;
    PrependIcon?: IconType;
    AppendIcon?: IconType;
    iconSize?: number;
    iconClassName?: string;
    disableHoverEffect?: boolean;
    disabledMessage?: string;
    totals?: number;
    tooltip?: TooltipButtonProps;
  };

export type TooltipButtonProps = {
  text: string;
  waitTime?: number;
  className?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
  alignOffset?: number;
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      iconClassName,
      variant,
      size,
      asChild = false,
      children,
      PrependIcon,
      AppendIcon,
      iconSize,
      disableHoverEffect,
      disabled,
      disabledMessage,
      totals,
      tooltip,
      type,
      style,
      ...restProps
    },
    ref,
  ) => {
    const classNames = cn(
      buttonVariants({ variant, size, status: restProps.status }),
      className,
      disableHoverEffect && 'hover:bg-transparent',
    );

    // Slot (asChild) requires exactly one React element child — do not wrap with icons/totals.
    // Spread `disabled` so it still reaches the child (Slot's typings omit button attrs).
    const button = asChild ? (
      <Slot className={classNames} ref={ref} style={style} {...restProps} {...{ disabled }}>
        {children}
      </Slot>
    ) : (
      <button className={classNames} ref={ref} disabled={disabled} type={type ?? 'button'} style={style} {...restProps}>
        {PrependIcon && <PrependIcon className={iconClassName} size={iconSize || 14} />}
        {children}
        {AppendIcon && <AppendIcon className={iconClassName} size={iconSize || 14} />}
        {totals !== undefined && (
          <div className="ml-auto">
            <TotalsChip total={totals} className="z-20" />
          </div>
        )}
      </button>
    );

    if (disabled && disabledMessage) {
      return (
        <Tooltip disableHoverableContent>
          <TooltipTrigger asChild>
            {/* Span: disabled buttons ignore pointer events; parent receives hover for the tooltip. */}
            <span className="inline-flex shrink-0">{button}</span>
          </TooltipTrigger>
          <TooltipContent>{disabledMessage}</TooltipContent>
        </Tooltip>
      );
    }

    if (tooltip) {
      return (
        <Tooltip disableHoverableContent>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent
            side={tooltip.side}
            sideOffset={tooltip.sideOffset}
            align={tooltip.align}
            alignOffset={tooltip.alignOffset}
          >
            {tooltip.text}
          </TooltipContent>
        </Tooltip>
      );
    }

    return button;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
